import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Button from '@mulesoft/anypoint-components/lib/Button';
import {
  handleBack,
  formatRelativeTime,
  parseSchedule,
  getStatusClass,
  formatScanSummary,
  authMapper,
  getStatsFromSummary,
  formatStatusText
} from '../../utils/utils';
import ScanConfigurationActions from '../ScanConfigurationActions';
import styles from './ScannerDetail.css';

const ScannerDetail = ({
  scanner,
  scannerId,
  organizationId,
  isFetching,
  scannerRuns,
  isFetchingScannerRuns,
  scannerRunsError,
  loadScannerRuns,
  clearScannerRuns,
  onRunNowClick
}) => {
  const history = useHistory();

  // Load scanner runs when component mounts or scanner changes
  React.useEffect(() => {
    if (organizationId && scannerId) {
      loadScannerRuns(organizationId, scannerId);
    }

    // Clear runs when unmounting
    return () => {
      clearScannerRuns();
    };
  }, [organizationId, scannerId, loadScannerRuns, clearScannerRuns]);

  // Memoized callback for back navigation
  const handleBackClick = React.useCallback(() => {
    handleBack(history);
  }, [history]);

  // Handle loading state
  if (isFetching) {
    return (
      <div className={styles.page} data-test-id="scanner-detail-loading">
        <p>Loading scanner details...</p>
      </div>
    );
  }

  // Handle scanner not found
  if (!scanner) {
    return (
      <div className={styles.page} data-test-id="scanner-detail-not-found">
        <h1>Scanner not found</h1>
        <p>The requested scanner could not be found.</p>
        <Button
          testId="not-found-back-button"
          kind="secondary"
          onClick={handleBackClick}
        >
          Back to List
        </Button>
      </div>
    );
  }

  return (
    <div
      className={styles.page}
      id="scannerDetailPage"
      data-test-id="scanner-detail-page"
    >
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.breadcrumbs}>
          <button
            type="button"
            className={styles.breadcrumbLink}
            onClick={handleBackClick}
            data-test-id="back-to-list"
          >
            Agent Scanners
          </button>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span
            className={styles.breadcrumbCurrent}
            data-test-id="scanner-name-breadcrumb"
          >
            {scanner.name}
          </span>
        </div>

        <div className={styles.detailHeader}>
          <div className={styles.detailTitleSection}>
            <h1
              className={styles.detailTitle}
              data-test-id="scanner-name-heading"
            >
              {scanner.name}
            </h1>
          </div>
          <ScanConfigurationActions
            variant="button"
            onRun={() => onRunNowClick(scanner, organizationId)}
            // TODO: Implement edit/delete in future PR
            onEdit={() => null}
            onDelete={() => null}
          />
        </div>
      </div>

      {/* Scanner Details Content */}
      <div className={styles.detailContent}>
        {/* Scanner information */}
        <div
          className={styles.detailSection}
          data-test-id="scanner-information-section"
        >
          <h3 className={styles.sectionTitle}>Scanner Information</h3>
          <div className={styles.detailSectionContent}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Scanner Name</span>
                <span data-test-id="scanner-name">{scanner.name}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status</span>
                <span
                  className={`${styles.statusBadge} ${getStatusClass(scanner.state, styles)}`}
                  data-test-id="scanner-status"
                >
                  {formatStatusText(scanner.state)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Next Run</span>
                <span data-test-id="scanner-next-run">
                  {scanner.nextRunAt
                    ? formatRelativeTime(scanner.nextRunAt)
                    : '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Run Schedule</span>
                <span data-test-id="scanner-schedule">
                  {parseSchedule(scanner.schedule) || '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Last Completed Run</span>
                <span data-test-id="scanner-last-completed-run">
                  {formatRelativeTime(scanner.lastSuccessfulRunAt)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Last Run Status</span>
                <span
                  className={
                    scanner.lastRunStatus
                      ? `${getStatusClass(scanner.lastRunStatus, styles)}`
                      : ''
                  }
                  data-test-id="scanner-last-run-status"
                >
                  {formatStatusText(scanner.lastRunStatus)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Created</span>
                <span data-test-id="scanner-created-at">
                  {formatRelativeTime(scanner.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Connection information */}
        <div
          className={styles.detailSection}
          data-test-id="connection-information-section"
        >
          <h3 className={styles.sectionTitle}>Connection Details</h3>
          <div className={styles.detailSectionContent}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Agent Provider</span>
                <span data-test-id="target-system">
                  {scanner.connection?.targetSystem?.name || '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Authentication</span>
                <span data-test-id="auth-method">
                  {authMapper(scanner.connection?.authScheme) || '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Last Connection Test</span>
                <span data-test-id="last-connection-test">
                  {formatRelativeTime(scanner.connection?.lastTestedAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Last Scan Results */}
        <div
          className={styles.detailSection}
          data-test-id="last-scan-results-section"
        >
          <h3 className={styles.sectionTitle}>
            Last Completed Scan -{' '}
            {formatRelativeTime(scanner.lastSuccessfulRunAt)}
          </h3>
          <div className={styles.detailSectionContentNoBorder}>
            {(() => {
              const stats = getStatsFromSummary(
                scanner.lastSuccessfulRunSummary
              );

              return (
                <div className={styles.syncResultsSummary}>
                  <div
                    className={styles.syncResultStat}
                    data-test-id="stat-added"
                  >
                    <div className={styles.statLabel}>Agents Added</div>
                    <div className={styles.statNumber}>{stats?.added ?? 0}</div>
                  </div>
                  <div
                    className={styles.syncResultStat}
                    data-test-id="stat-updated"
                  >
                    <div className={styles.statLabel}>Agents Updated</div>
                    <div className={styles.statNumber}>
                      {stats?.updated ?? 0}
                    </div>
                  </div>
                  <div
                    className={styles.syncResultStat}
                    data-test-id="stat-removed"
                  >
                    <div className={styles.statLabel}>Agents Removed</div>
                    <div className={styles.statNumber}>
                      {stats?.removed ?? 0}
                    </div>
                  </div>
                  <div
                    className={styles.syncResultStat}
                    data-test-id="stat-unchanged"
                  >
                    <div className={styles.statLabel}>Agents in Exchange</div>
                    <div className={styles.statNumber}>
                      {stats?.unchanged ?? 0}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Scan history */}
        <div
          className={styles.detailSection}
          data-test-id="scan-history-section"
        >
          <h3 className={styles.sectionTitle}>Scan History</h3>
          <div className={styles.detailSectionContent}>
            {isFetchingScannerRuns && (
              <div className={styles.loadingRuns} data-test-id="runs-loading">
                Loading scan history...
              </div>
            )}
            {!isFetchingScannerRuns && scannerRunsError && (
              <div className={styles.errorRuns} data-test-id="runs-error">
                Error loading scan history: {scannerRunsError.message}
              </div>
            )}
            {!isFetchingScannerRuns &&
              !scannerRunsError &&
              scannerRuns.length === 0 && (
                <div className={styles.emptyRuns} data-test-id="runs-empty">
                  No scan history available.
                </div>
              )}
            {!isFetchingScannerRuns &&
              !scannerRunsError &&
              scannerRuns.length > 0 && (
                <div className={styles.scanHistoryList}>
                  {scannerRuns.map((run, index) => (
                    <div
                      key={run.createdAt || index}
                      className={styles.historyItem}
                      data-test-id={`history-item-${index}`}
                    >
                      <div className={styles.historyTime}>
                        {formatRelativeTime(run.startedAt)}
                      </div>
                      <div
                        className={`${styles.historyStatus} ${getStatusClass(run.status, styles)}`}
                        data-test-id={`history-status-${index}`}
                      >
                        {formatStatusText(run.status)}
                      </div>
                      <div
                        className={styles.historySummary}
                        data-test-id={`history-summary-${index}`}
                      >
                        {formatScanSummary(run.summary)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

ScannerDetail.propTypes = {
  scanner: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    connectionId: PropTypes.string,
    state: PropTypes.oneOf(['RUNNING', 'IDLE', 'COMPLETED', 'DISABLED']),
    status: PropTypes.oneOf([
      'SUCCESS',
      'CLOSE',
      'RUNNING',
      'IDLE',
      'ERROR',
      'COMPLETED'
    ]),
    lastRunAt: PropTypes.string,
    lastRunStatus: PropTypes.string,
    lastSuccessfulRunAt: PropTypes.string,
    lastSuccessfulRunSummary: PropTypes.shape({
      status: PropTypes.string,
      description: PropTypes.string,
      stats: PropTypes.shape({
        added: PropTypes.number,
        updated: PropTypes.number,
        removed: PropTypes.number,
        unchanged: PropTypes.number
      })
    }),
    createdAt: PropTypes.string,
    nextRunAt: PropTypes.string,
    parameters: PropTypes.any,
    runPolicy: PropTypes.any,
    notes: PropTypes.string,
    schedule: PropTypes.string,
    connection: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      authScheme: PropTypes.string,
      lastTestedAt: PropTypes.string,
      status: PropTypes.string,
      targetSystem: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string
      })
    })
  }),
  scannerId: PropTypes.string,
  organizationId: PropTypes.string,
  isFetching: PropTypes.bool,
  scannerRuns: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
      startedAt: PropTypes.string,
      endedAt: PropTypes.string,
      createdAt: PropTypes.string,
      summary: PropTypes.shape({
        added: PropTypes.number,
        updated: PropTypes.number,
        removed: PropTypes.number,
        unchanged: PropTypes.number,
        issuesFound: PropTypes.arrayOf(PropTypes.string)
      })
    })
  ),
  isFetchingScannerRuns: PropTypes.bool,
  scannerRunsError: PropTypes.shape({
    message: PropTypes.string
  }),
  loadScannerRuns: PropTypes.func,
  clearScannerRuns: PropTypes.func,
  onRunNowClick: PropTypes.func
};

ScannerDetail.defaultProps = {
  scannerRuns: [],
  isFetchingScannerRuns: false,
  scannerRunsError: null,
  loadScannerRuns: () => {},
  clearScannerRuns: () => {}
};

export default ScannerDetail;
